import * as React from "preact";
import { FunctionComponent } from "preact";
import { useState, useEffect } from "preact/hooks";

import cx from "classnames";

import { pageTypes } from "../../../../helpers/constants";
import Concertina from "../../../components/Concertina";
import Meta from "../Meta";
import RelatedArticle from "../RelatedArticle";
import FasterOnline from "../FasterOnline";
import SavedItemsButton from "../../../components/SavedItemsButton";

export interface ISuperCategory {
  superCategoryId: string;
  displayName: string;
}

export interface ISuperCategories {
  order: ISuperCategory[];
  maxResults: number;
}

export interface ICompleteOnlineUrls {
  roleId: string;
  completeOnlineUrl: string;
}

export interface IFasterOnlineRolesUrls {
  roleId: string;
  fasterOnlineUrl: string;
}

export type PageType =
  | typeof pageTypes.FAQ
  | typeof pageTypes.WEBPAGE
  | typeof pageTypes.DOCUMENT
  | typeof pageTypes.ARTICLE
  | typeof pageTypes.ASSET
  | typeof pageTypes.PEOPLE;

export interface IResult {
  contentId: string | null;
  pageType: PageType;
  title: string;
  description?: string;
  target?: string;
  additionalText?: string | null;
  answerText?: string | null;
  authors?: string;
  filters?: string[];
  contentUrl?: string;
  fileType?: string | null;
  jobLocation?: string | null;
  jobTitle?: string | null;
  publishDate?: string;
  lastReviewDate?: string;
  questionText?: string | null;
  readTime?: number;
  watchTime?: number;
  reference?: string | null;
  searchImageUrl?: string | null;
  superCategoryList: string[];
  hasExternalUrl?: boolean;
  relatedDocument?: IResult[];
  documentType?: string;
  emailLabel?: string;
  email?: string;
  phoneNumber?: string;

  fundEventType?: string | null;
  companiesImpacted?: string | null;
  effectiveDate?: string | null;
  changeDescription?: string | null;
  oldLabel?: string | null;
  oldDescription?: string | null;
  newLabel?: string | null;
  newDescription?: string | null;
  defaultAlternativeFundLabel?: string | null;
  defaultAlternativeFundDescription?: string | null;

  hasCompleteOnlineUrl: boolean;
  completeOnlineText: string | null;
  completeOnlineUrls: ICompleteOnlineUrls[] | null;
  completeOnlineOpenInSameTab: boolean;

  fasterOnlineModalActive: boolean;
  fasterOnlineUrls: IFasterOnlineRolesUrls[] | null;
  fasterOnlineModalTitle: string | null;
  fasterOnlineModalPrimaryText: string | null;
  fasterOnlineSecondaryText: string | null;
  fasterOnlineModalCtaText: string | null;
  fasterOnlineOpenInSameTab: boolean;
  fasterOnlineCtaOpenInSameTab: boolean;

  categoryList?: any[];
  filterList?: any[];
  saved?: boolean;
}

export interface IResultItem {
  id: string;
  title: string;
  item: IResult;
  settings: any;
  includeRelated: boolean;
  tabId: string;
  dataSaveStates?: { contentId: string, state: boolean }[];
  fetchDataSaveLoading?: boolean;
}

const ResultItem: FunctionComponent<IResultItem> =
  ({
     title,
     item,
     settings,
     id,
     includeRelated,
     tabId,
     dataSaveStates,
     fetchDataSaveLoading,
   }) => {
    const {
      description,
      contentUrl,
      target,
      searchImageUrl,
      additionalText,
      fileType,
      pageType,
      emailLabel,
      email,
      phoneNumber,
    } = item;
    const {
      thumbnailFallback,
      thumbnails,
      publishDateLabel,
      savedItemsEnable,
      updateSaveStateApiUrl,
      faqFeedbackEnable,
    } = settings;
    const documentType = pageType === pageTypes.DOCUMENT;
    const articleType = pageType === pageTypes.ARTICLE;
    const peopleType = pageType === pageTypes.PEOPLE;
    const faqType = pageType === pageTypes.FAQ;
    const videoType = pageType === pageTypes.VIDEO;
    const teamType = pageType === pageTypes.TEAM;
    const assetType = pageType === pageTypes.ASSET;
    const hasImage = (peopleType || articleType || videoType || assetType ) && thumbnails;
    const [showModal, setShowModal] = useState(false);
    const [isSaved, setIsSaved] = useState<boolean>(false);
    const [toggleSaveLoading, setToggleSaveLoading] = useState<boolean>(false);
    const renderPic = () => {
      if (searchImageUrl || thumbnailFallback) {
        return (
          <a
            href={contentUrl}
            className="media-block__figure media-block__figure-left search-results__image-container"
            target={target || ""}
          >
            <img
              src={searchImageUrl || thumbnailFallback}
              className="search-results__image"
              alt={peopleType ? "Headshot of " + title : title}
            />
          </a>
        );
      }
    };

    useEffect(() => {
      if (!dataSaveStates || !dataSaveStates.length) return;
      const saveItem = dataSaveStates.find(i => i.contentId.toLowerCase() === item?.contentId?.toLowerCase());

      if (!saveItem || !saveItem.state) {
        setIsSaved(false);
        return;
      }

      setIsSaved(true);
    }, [dataSaveStates]);

    const renderFileType = () => {
      if (!fileType) {
        return;
      }
      return <span
        className="search-results__file-type badge">{fileType}</span>;
    };

    const updateShowModal = (state: boolean, e: any) => {
      e.preventDefault();
      setShowModal(state);
    };

    const onToggleSavedButton = async () => {
      if (!updateSaveStateApiUrl || peopleType) return;
      try {
        setToggleSaveLoading(true);
        setIsSaved(prev => !prev);

        const response = await fetch(updateSaveStateApiUrl, {
          method: "POST",
          body: JSON.stringify({ state: !isSaved, contentId: item.contentId }),
          headers: {
            "Content-Type": "application/json",
            "Accept": "application:json",
          },
        });

        if (response.status !== 200) {
          throw new Error(`There was a problem. Status Code: ${response.status}`);
        }

      } catch (error) {
        console.log(error);
        setIsSaved(prev => !prev);
      } finally {
        setToggleSaveLoading(false);
      }
    };

    if (faqType) {
      return (
        <Concertina
          title={title}
          description={description}
          additionalText={additionalText}
          id={title.replace(/\s+/g, "")}
          isRichtext={true}
          renderFaqFeedback={faqFeedbackEnable && faqType}
          savedItemsButton={savedItemsEnable && {
            text: isSaved ? "Saved" : "Save",
            active: isSaved,
            contentId: item.contentId,
            onClick: onToggleSavedButton,
            disabled: fetchDataSaveLoading || toggleSaveLoading,
          }}
        />
      );
    }


    return (
      <div className={cx("media-block", { "media-block--grid": hasImage })}>
        {hasImage ? renderPic() : ""}
        {item.fasterOnlineModalActive && (
          <FasterOnline
            item={item}
            show={showModal}
            changeShowModal={setShowModal}
            id={(id + item.publishDate + tabId).replace(/\W/g, "")}
          />
        )}
        <div className="media-block__heading">
          <h3
            className={(includeRelated ? "search-results__heading" : "related-results__heading") + " h4"}>
            {contentUrl ? (
              <a
                href={item.fasterOnlineModalActive ? "" : contentUrl}
                target={item.fasterOnlineModalActive ? "" : target}
                className={includeRelated ? "search-results__heading-link" : "related-results__heading-link"}
                onClick={item.fasterOnlineModalActive ? (e: any) => updateShowModal(true, e) : undefined}
              >
                {title}
              </a>
            ) : (
              title
            )}
            {documentType ? renderFileType() : ""}
          </h3>

          {savedItemsEnable && !peopleType &&
            <SavedItemsButton
              disabled={fetchDataSaveLoading || toggleSaveLoading}
              text={isSaved ? "Saved" : "Save"} active={isSaved}
              onClick={onToggleSavedButton}
              contentId={item?.contentId} />}
        </div>

        <div className="media-block__body search-results__text">
          <Meta
            item={item}
            publishDateLabel={publishDateLabel}
          ></Meta>

          {description && <p>{description}</p>}
          {teamType && <div>
            {emailLabel && email &&
              <a
                href={`mailto:${email}`}
                className="search-results__email with-icon link--base link--with-icon icon--email"
              >
                <span className="link--with-underline">{emailLabel}</span>
              </a>}
            {phoneNumber && <a
              href={`tel:${phoneNumber}`}
              className="search-results__phone-number with-icon link--base link--with-icon icon--contact"
            >
              <span className="link--with-underline">{phoneNumber}</span>
            </a>}
          </div>}
          {!!item.relatedDocument && includeRelated && !!item.relatedDocument.length && (
            <RelatedArticle
              item={item}
              id={id}
              title={title}
              settings={settings}
              tabId={tabId}
            />
          )}
        </div>
      </div>
    );
  };

export default ResultItem;
